import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toastError, errorMessage } from '../../lib/toast';

/**
 * Tools panel v2 for the User Permissions drawer.
 *
 * Changes vs v1:
 *   - Filters reduced to two axes:
 *       1) Segmented status (mutually exclusive): All · Granted · Not granted
 *       2) Source toggles (multi-select):       Direct · Dept default · Managers only
 *     (Was: 5 mixed chips that combined status + source on one axis.)
 *   - True switch component (role="switch") instead of a bare checkbox so the
 *     meaning ("flip access on/off") is unambiguous.
 *   - Pill column replaced with inline source line under the tool name —
 *     less width pressure, easier to scan.
 *   - Loading skeleton matches the loaded row height (~52px) so the layout
 *     doesn't jump.
 *
 * managers_only tools are still read-only (cannot be granted to employees).
 */
export default function UserPermissionsToolsPanel({ user, onChanged, getImageUrl, readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all|granted|not_granted
  const [sourceFilter, setSourceFilter] = useState(() => new Set()); // direct|dept|managers_only
  const [pendingId, setPendingId] = useState(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/users/${user.id}/tool-permissions`, {
        withCredentials: true,
      });
      setRows(r.data || []);
    } catch (err) {
      setError('Could not load tool permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [user?.id]);

  const counts = useMemo(() => {
    const enabled = rows.filter((r) => r.is_assigned).length;
    // "Via dept" now includes both `all`-visibility dept defaults AND the
    // implicit grant a manager gets from managing a department whose
    // managers_only default this tool is.
    const viaDept = rows.filter(
      (r) => r.is_assigned && (r.dept_default_in.length > 0 || (r.manager_default_in || []).length > 0)
    ).length;
    const direct = enabled - viaDept;
    const managersOnly = rows.filter((r) => r.dept_visibility === 'managers_only').length;
    return { total: rows.length, enabled, direct, viaDept, managersOnly };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sources = sourceFilter;
    return rows.filter((r) => {
      if (q && !r.name.toLowerCase().includes(q) && !(r.category_name || '').toLowerCase().includes(q)) {
        return false;
      }
      if (statusFilter === 'granted' && !r.is_assigned) return false;
      if (statusFilter === 'not_granted' && r.is_assigned) return false;
      if (sources.size > 0) {
        const viaManagerRole = (r.manager_default_in || []).length > 0;
        const hasExplicitRow = r.is_assigned && r.assignment_id != null;
        const isDept = r.is_assigned && (r.dept_default_in.length > 0 || viaManagerRole);
        const isDirect = hasExplicitRow && r.dept_default_in.length === 0 && !viaManagerRole;
        const isManagersOnly = r.dept_visibility === 'managers_only';
        const matched =
          (sources.has('direct') && isDirect) ||
          (sources.has('dept') && isDept) ||
          (sources.has('managers_only') && isManagersOnly);
        if (!matched) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, sourceFilter]);

  const grouped = useMemo(() => {
    const out = new Map();
    for (const r of filtered) {
      const k = r.category_name || 'Uncategorized';
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(r);
    }
    return out;
  }, [filtered]);

  const targetCanReceiveManagersOnly =
    !!user && (user.is_admin || (user.roles || []).includes('dept_manager'));

  const toggle = async (row) => {
    if (readOnly) return;
    if (row.dept_visibility === 'managers_only' && !targetCanReceiveManagersOnly) return;
    setPendingId(row.id);
    try {
      if (row.is_assigned) {
        await axios.delete(`${API_BASE_URL}/api/user_tools/${row.assignment_id}`, { withCredentials: true });
      } else {
        await axios.post(
          `${API_BASE_URL}/api/user_tools`,
          { user_id: user.id, tool_id: row.id },
          { withCredentials: true }
        );
      }
      await load();
      onChanged?.();
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      toastError(apiMsg || errorMessage(err, "Couldn't update access. Try again."));
    } finally {
      setPendingId(null);
    }
  };

  const toggleSource = (key) => {
    setSourceFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!user) return null;

  return (
    <section className="upp">
      <header className="upp-header">
        <div>
          <h3>Tools</h3>
          <p className="upp-summary" aria-live="polite">
            <strong>{counts.enabled}</strong> of {counts.total} granted
            {counts.managersOnly > 0 && <> · {counts.managersOnly} managers-only</>}
          </p>
        </div>
      </header>

      <div className="upp-searchbar">
        <svg className="upp-searchbar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
        </svg>
        <input
          type="search"
          placeholder="Search tools to assign by name or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="upp-searchbar-input"
          aria-label="Search tools"
        />
        {search && (
          <button
            type="button"
            className="upp-searchbar-clear"
            onClick={() => setSearch('')}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      <div className="upp-controls">
        <div className="upp-segmented" role="tablist" aria-label="Status">
          {[
            ['all', `All (${counts.total})`],
            ['granted', `Granted (${counts.enabled})`],
            ['not_granted', `Not granted (${counts.total - counts.enabled})`],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={statusFilter === k}
              className={`upp-seg ${statusFilter === k ? 'is-active' : ''}`}
              onClick={() => setStatusFilter(k)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="upp-sources" role="group" aria-label="Source filter">
          {[
            ['direct', 'Direct', counts.direct],
            ['dept', 'Dept default', counts.viaDept],
            ['managers_only', 'Managers only', counts.managersOnly],
          ].map(([k, label, n]) => (
            <button
              key={k}
              type="button"
              aria-pressed={sourceFilter.has(k)}
              className={`upp-srcpill upp-srcpill--${k} ${sourceFilter.has(k) ? 'is-active' : ''}`}
              onClick={() => toggleSource(k)}
            >
              {label}
              <span className="upp-srcpill-count">{n}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div className="upp-error" role="alert">{error}</div>}
      {loading ? (
        <div className="upp-list" aria-busy="true" aria-label="Loading tool list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="upp-cat">
              <div className="upp-cat-title upp-skel-line" />
              <div className="upp-rows">
                {Array.from({ length: 2 }).map((__, j) => (
                  <div key={j} className="upp-row upp-skel-row" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="upp-empty">
          {search || statusFilter !== 'all' || sourceFilter.size > 0
            ? 'No tools match your filter.'
            : 'No tools defined yet.'}
        </div>
      ) : (
        <ul className="upp-list" role="list">
          {[...grouped.entries()].map(([cat, items]) => (
            <li key={cat} className="upp-cat">
              <h4 className="upp-cat-title">
                {cat}
                <span className="upp-cat-count">
                  {items.filter((r) => r.is_assigned).length}/{items.length}
                </span>
              </h4>
              <ul className="upp-rows" role="list">
                {items.map((row) => {
                  const isPending = pendingId === row.id;
                  const isManagersOnly = row.dept_visibility === 'managers_only';
                  const managerDeptIn = row.manager_default_in || [];
                  // Implicit grant: user manages a department where this tool
                  // is a managers_only default. They see the tool in their
                  // dept-apps catalog without an explicit user_tools row.
                  const viaManagerRole = managerDeptIn.length > 0;
                  // Has an explicit user_tools row.
                  const hasExplicitRow = row.is_assigned && row.assignment_id != null;
                  // Restricted = managers-only AND target user cannot receive
                  // it AND they don't already have it via being a manager.
                  const restricted = isManagersOnly && !targetCanReceiveManagersOnly && !viaManagerRole;
                  const assignedViaDept = hasExplicitRow && row.dept_default_in.length > 0;
                  const assignedDirect = hasExplicitRow && row.dept_default_in.length === 0;
                  const sourceText = restricted
                    ? 'Locked · managers only'
                    : viaManagerRole && !hasExplicitRow
                      ? `Granted via managing ${managerDeptIn.join(', ')}`
                      : isManagersOnly && targetCanReceiveManagersOnly && !row.is_assigned
                        ? 'Managers-only · admin can still grant'
                        : assignedViaDept
                          ? `Granted via ${row.dept_default_in.join(', ')}`
                          : assignedDirect
                            ? 'Direct grant'
                            : 'Not granted';
                  const sourceClass = restricted
                    ? 'upp-srcline--lock'
                    : viaManagerRole && !hasExplicitRow
                      ? 'upp-srcline--dept'
                      : isManagersOnly && !row.is_assigned
                        ? 'upp-srcline--lock'
                        : assignedViaDept
                          ? 'upp-srcline--dept'
                          : assignedDirect
                            ? 'upp-srcline--direct'
                            : 'upp-srcline--off';
                  // Switch disabled when the access is purely derived (manager
                  // role) — you remove it by removing the manager role, not by
                  // toggling user_tools.
                  const switchDisabled = readOnly || restricted || isPending || (viaManagerRole && !hasExplicitRow);
                  const switchTitle = readOnly
                    ? 'You do not have permission to change tool access.'
                    : restricted
                      ? 'This tool is managers-only. Only admins or department managers can receive it directly.'
                      : viaManagerRole && !hasExplicitRow
                        ? `This user has access because they manage ${managerDeptIn.join(', ')}. Remove their manager role to revoke.`
                        : isManagersOnly
                          ? 'Managers-only tool — granting directly because this user is an admin/manager.'
                          : undefined;
                  return (
                    <li key={row.id} className={`upp-row ${row.is_assigned ? 'is-on' : ''} ${restricted ? 'is-locked' : ''}`}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={row.is_assigned}
                        aria-label={`${row.is_assigned ? 'Revoke' : 'Grant'} access to ${row.name}`}
                        className="upp-switch"
                        onClick={() => toggle(row)}
                        disabled={switchDisabled}
                        title={switchTitle}
                      >
                        <span className="upp-switch-thumb" aria-hidden="true" />
                      </button>
                      <div className="upp-icon" aria-hidden="true">
                        {row.icon_url ? (
                          <img src={getImageUrl(row.icon_url)} alt="" />
                        ) : (
                          <span>{row.name.slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="upp-meta">
                        <div className="upp-name-row">
                          <span className="upp-name">{row.name}</span>
                          <span className="upp-tags">
                            {assignedDirect && (
                              <span className="upp-tag upp-tag--direct" title="Granted directly to this user.">Direct</span>
                            )}
                            {isManagersOnly && (
                              <span className="upp-tag upp-tag--lock" title="This tool is marked managers-only in at least one department.">Managers only</span>
                            )}
                          </span>
                        </div>
                        <div className={`upp-srcline ${sourceClass}`}>{sourceText}</div>
                      </div>
                      {restricted && (
                        <span className="upp-lock-label" aria-hidden="true">Locked</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
